import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { CallsRouteQueryParameters } from '~/pages/Calls/CallsRoutingTypes'
import { stringifyQueryParams } from '~/utils/apiQueryUtils'
import { hasValue } from '~/utils/common'

import {
  addDays,
  addMonths,
  getEndOfDay,
  getEndOfMonth,
  getEndOfWeek,
  getStartOfDay,
  getStartOfMonth,
  getStartOfWeek,
  isSameDate,
} from '@mangovoice/core'
import { TimePeriodRange as CoreTimePeriodRange } from '@mangovoice/core'

export type SearchValue = {
  value: string
  label: string
}

// Freeze the empty state, so nobody can accidentally modify the object later
const empty = { value: '', label: '' } as SearchValue
Object.freeze(empty)

export const SearchValue = {
  empty: empty,
  fromString: (value: string) => ({ value, label: value }),
  fromLabel: (label: string) => ({ value: label, label }),
  fromValueAndLabel: (value: string, label: string) => ({ value, label }),
}

export enum DateUnit {
  Days = 'day',
  Months = 'month',
  Years = 'year',
}

export type DateRange = object & {
  before?: Date
  after?: Date
}

export const DateRange = {
  empty: () => ({ before: undefined, after: undefined }) as DateRange,
  today: () => {
    const after = getStartOfDay(new Date())
    const before = getEndOfDay(after)

    return {
      after,
      before,
    } as DateRange
  },
  yesterday: () => {
    const after = addDays(getStartOfDay(new Date()), -1)
    const before = getEndOfDay(after)

    return {
      after,
      before,
    } as DateRange
  },
  last7Days: () => {
    // Create today's date at end of day (11:59:59.999 PM)
    const today = getEndOfDay(new Date())
    // Create date 7 days ago at start of day (12:00:00.000 AM)
    const sevenDaysAgo = addDays(getStartOfDay(new Date()), -7)

    return {
      after: sevenDaysAgo,
      before: today,
    } as DateRange
  },
  last14Days: () => {
    // Create today's date at end of day
    const today = getEndOfDay(new Date())
    // Create date 14 days ago at start of day
    const fourteenDaysAgo = addDays(getStartOfDay(new Date()), -14)

    return {
      after: fourteenDaysAgo,
      before: today,
    } as DateRange
  },
  last30Days: () => {
    // Create today's date at end of day
    const today = getEndOfDay(new Date())
    // Create date 30 days ago at start of day
    const thirtyDaysAgo = addDays(getStartOfDay(new Date()), -30)

    return {
      after: thirtyDaysAgo,
      before: today,
    } as DateRange
  },
  last60Days: () => {
    // Create today's date at end of day
    const today = getEndOfDay(new Date())
    // Create date 60 days ago at start of day
    const sixtyDaysAgo = addDays(getStartOfDay(new Date()), -60)

    return {
      after: sixtyDaysAgo,
      before: today,
    } as DateRange
  },
  last90Days: () => {
    // Create today's date at end of day
    const today = getEndOfDay(new Date())
    // Create date 90 days ago at start of day
    const ninetyDaysAgo = addDays(getStartOfDay(new Date()), -90)

    return {
      after: ninetyDaysAgo,
      before: today,
    } as DateRange
  },
  thisWeek: () => {
    //compute start of week (monday)
    const after = getStartOfWeek(new Date())
    const before = getEndOfWeek(after)

    return {
      after,
      before,
    } as DateRange
  },
  lastWeek: () => {
    const after = addDays(getStartOfWeek(new Date()), -7)
    const before = getEndOfWeek(after)

    return {
      after,
      before,
    } as DateRange
  },
  thisMonth: () => {
    const after = getStartOfMonth(new Date())
    const before = getEndOfMonth(after)

    return {
      after,
      before,
    } as DateRange
  },
  lastMonth: () => {
    const after = addMonths(getStartOfMonth(new Date()), -1)
    const before = getEndOfMonth(after)

    return {
      after,
      before,
    } as DateRange
  },
  areEqual: (a?: DateRange, b?: DateRange) => {
    // If references are the same, they're equal
    if (a === b) return true

    // If one is undefined and the other isn't, they're not equal
    if (!a || !b) return false

    // Check before dates for equality
    const beforeEqual = isSameDate(a.before, b.before)

    // Check after dates for equality
    const afterEqual = isSameDate(a.after, b.after)

    // Both must be equal for the DateRanges to be equal
    return beforeEqual && afterEqual
  },
}
Object.freeze(DateRange)

enum DateRangeIdentifier {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last_7_days',
  Last14Days = 'last_14_days',
  Last30Days = 'last_30_days',
  Last60Days = 'last_60_days',
  Last90Days = 'last_90_days',
}
export const useDateRangeWithSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const dateRangeParam = searchParams.get('date_range')
  const after = searchParams.get('aftr')
  const before = searchParams.get('bfr')
  const [dateTimeRange, setDateTimeRange] = useState<DateRange | undefined>(
    undefined,
  )
  useEffect(() => {
    let _dateTimeRange: DateRange | undefined = undefined
    if (dateRangeParam) {
      switch (dateRangeParam) {
        case 'today':
          _dateTimeRange = DateRange.today()
          break
        case 'yesterday':
          _dateTimeRange = DateRange.yesterday()
          break
        case 'last_7_days':
          _dateTimeRange = DateRange.last7Days()
          break
        case 'last_14_days':
          _dateTimeRange = DateRange.last14Days()
          break
        case 'last_30_days':
          _dateTimeRange = DateRange.last30Days()
          break
        case 'last_60_days':
          _dateTimeRange = DateRange.last60Days()
          break
        case 'last_90_days':
          _dateTimeRange = DateRange.last90Days()
          break
        default:
          break
      }
    } else {
      if (after && before) {
        _dateTimeRange = {
          after: new Date(Number(after)),
          before: new Date(Number(before)),
        }
      }
    }
    if (_dateTimeRange) {
      setDateTimeRange(_dateTimeRange)
    }
  }, [dateRangeParam, after, before])

  const handleSetDateRange = (dateRange: DateRange) => {
    const existingParams = Object.fromEntries(searchParams)
    delete existingParams.date_range
    delete existingParams.aftr
    delete existingParams.bfr

    let dateRangeIdentifier: DateRangeIdentifier | undefined
    const routeQueryParams: CallsRouteQueryParameters = {}
    if (DateRange.areEqual(dateRange, DateRange.today())) {
      dateRangeIdentifier = DateRangeIdentifier.Today
    } else if (DateRange.areEqual(dateRange, DateRange.yesterday())) {
      dateRangeIdentifier = DateRangeIdentifier.Yesterday
    } else if (DateRange.areEqual(dateRange, DateRange.last7Days())) {
      dateRangeIdentifier = DateRangeIdentifier.Last7Days
    } else if (DateRange.areEqual(dateRange, DateRange.last14Days())) {
      dateRangeIdentifier = DateRangeIdentifier.Last14Days
    } else if (DateRange.areEqual(dateRange, DateRange.last30Days())) {
      dateRangeIdentifier = DateRangeIdentifier.Last30Days
    } else if (DateRange.areEqual(dateRange, DateRange.last60Days())) {
      dateRangeIdentifier = DateRangeIdentifier.Last60Days
    } else if (DateRange.areEqual(dateRange, DateRange.last90Days())) {
      dateRangeIdentifier = DateRangeIdentifier.Last90Days
    } else if (hasValue(dateRange.after) && hasValue(dateRange.before)) {
      routeQueryParams.aftr = dateRange.after?.getTime()
      routeQueryParams.bfr = dateRange.before?.getTime()
    }

    if (dateRangeIdentifier) {
      routeQueryParams.date_range = dateRangeIdentifier
    }

    setSearchParams(
      stringifyQueryParams({ ...existingParams, ...routeQueryParams }),
    )
  }
  return [dateTimeRange, handleSetDateRange] as const
}

export const TimePeriodRange = {
  empty: { before: undefined, after: undefined } as CoreTimePeriodRange,
}
Object.freeze(TimePeriodRange.empty)

export interface FilterItem<T = unknown> {
  title: string
  data?: T
  isSelected?: boolean
  isDisabled?: boolean
  icon?: SVGComponent
}

export interface FilterRow<T = unknown> {
  title?: string
  items: FilterItem<T>[]
}

export interface FilterSection<T = unknown> {
  title: string
  rows: FilterRow<T>[]
}

export interface SelectedFilterData {
  sectionTitle: string
  rowTitle?: string
  itemTitle: string
  item: FilterItem
}

export enum SortDirection {
  Ascending = 'ascending',
  Descending = 'descending',
}
